import React, { Suspense, useEffect } from "react";
import { Provider, useDispatch } from "react-redux";
import { store } from "@/store/store";
import RoutesComponent from "RoutesComponent";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import {
  auth0Audience,
  auth0ClientID,
  auth0Connection,
  auth0Domain,
  JWTToken,
} from "./constant/AppConstant";
import {
  setIdToken,
  setUserIsRendered,
  setUserToken,
} from "./components/User/userSlice";
import { ToastContainer } from "react-toastify";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import "./styles/globals.css";
import { Toaster } from "./components/ui/toaster";
import { isTokenExpired } from "./helpers/authUtils";

export let getAuthToken = null;

function AppContent() {
  const dispatch = useDispatch();

  const location = useLocation();
  const {
    isAuthenticated,
    getAccessTokenSilently,
    user,
    isLoading,
    loginWithRedirect,
    getIdTokenClaims,
  } = useAuth0();

  getAuthToken = getAccessTokenSilently;

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Allow public guest pages without auth
        if (location.pathname.startsWith('/guest/')) {
          dispatch(setUserIsRendered(true));
          return;
        }

        // Proactive token expiration check
        const storedToken = localStorage.getItem(JWTToken);
        if (storedToken) {
          const parsedToken = JSON.parse(storedToken);
          if (isTokenExpired(parsedToken.accessToken) || isTokenExpired(parsedToken.idToken)) {
            console.log("Token expired during initialization. Redirecting to login...");
            localStorage.removeItem(JWTToken);
            localStorage.removeItem("IdToken");
            if (location.pathname !== '/login') {
              loginWithRedirect();
              return;
            }
          }
        }

        // If we have the code parameter, we're in the callback
        const params = new URLSearchParams(location.search);
        const hasCode = params.has("code");

        if (hasCode && isAuthenticated && user) {
          console.log("Processing callback with code...");
          const token = await getAccessTokenSilently();
          const idToken = await getIdTokenClaims();
          console.log("Token received:", !!token);

          const tokenData = {
            accessToken: token,
            idToken: idToken.__raw,
            user: user,
          };

          localStorage.setItem(JWTToken, JSON.stringify(tokenData));
          localStorage.setItem("IdToken", idToken.__raw);
          dispatch(setUserToken(tokenData.accessToken));
        } else if (!hasCode && !isAuthenticated) {
          // Normal initialization
          if (storedToken) {
            const parsedToken = JSON.parse(storedToken);
            dispatch(setIdToken(parsedToken.idToken));
            dispatch(setUserToken(parsedToken.accessToken));
            dispatch(setUserIsRendered(true));
            // check if token is expired via silent refresh
            try {
              const token = await getAccessTokenSilently();
              if (token) {
                dispatch(setUserToken(token));
                parsedToken.accessToken = token;
                localStorage.setItem(JWTToken, JSON.stringify(parsedToken));
              } else if (!location.pathname.startsWith('/guest/')) {
                loginWithRedirect();
              }
            } catch (e) {
              if (!location.pathname.startsWith('/guest/')) {
                loginWithRedirect();
              }
            }
          } else if (!location.pathname.startsWith('/guest/') && location.pathname !== '/login') {
            loginWithRedirect();
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (!location.pathname.startsWith('/guest/')) {
          loginWithRedirect();
        }
      } finally {
        dispatch(setUserIsRendered(true));
      }
    };

    if (!isLoading) {
      initializeAuth();
    }
  }, [
    isAuthenticated,
    user,
    getAccessTokenSilently,
    dispatch,
    isLoading,
    location,
    getIdTokenClaims,
  ]);

  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      }
    >
      <Toaster />
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} />
      {user ? <Layout /> : <></>}
      <main className={user?.["http://learnido-app/roleId"] === "1" ? "admin-main-content" : ""}>
        <RoutesComponent />
      </main>
    </Suspense>
  );
}

function App() {
  return (
    <Provider store={store}>
      <Router>
        <Auth0Provider
          domain={auth0Domain}
          clientId={auth0ClientID}
          authorizationParams={{
            redirect_uri: window.location.origin,
            audience: auth0Audience,
            scope: "openid profile email",
            connection: auth0Connection,
          }}
          onRedirectCallback={(appState, user) => {
            console.log("appState", appState);
            console.log("user", user);
            if (user["http://learnido-app/roleId"] == "1") {
              console.log("User is Admin");
            }
          }}
          useRefreshTokens={true}
          cacheLocation="localstorage"
        >
          <AppContent />
        </Auth0Provider>
      </Router>
    </Provider>
  );
}

export default App;
